// The shared mode filter, as a composable: ONE stored setting every screen reads.
//
// Pure rules are `src/mode-filter.js`. The choices are kept as explicit
// `{ 'melbourne:tram': false }` entries in preferences (the same store the other
// switches use); nothing is written until somebody taps a chip, so the defaults
// can keep following what the phone watches.

import { computed, ref } from 'vue';
import { prefs } from './core/storage.js';
import { city, setModeOn } from './app-state.js';
import { markModeIntroSeen, modeOn } from './mode-state.js';
import {
  activeIds, filterChips, flipped, hiddenNotes, partitionRows, resolveOn, choiceKey,
  effectiveOn, moveId, orderChips, priorityIds,
} from './mode-filter.js';

export const FILTER_KEY = 'lateagain:mode-filter:v1';
/** The order each city's modes are ranked in: `{ melbourne: ['train', 'bus', 'tram'] }`. */
export const ORDER_KEY = 'lateagain:mode-order:v1';
// The planner's own switch, before there was one shared setting.
const OLD_PLAN_KEY = 'lateagain:plan-modes:v1';

function read() {
  const raw = prefs[FILTER_KEY];
  return raw && typeof raw === 'object' ? { ...raw } : {};
}

// One ref for the app: toggling in one place is seen in every other, live.
const stored = ref(read());

const readOrder = () => {
  const raw = prefs[ORDER_KEY];
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...raw } : {};
};
const storedOrder = ref(readOrder());

// THE BOARD'S TAPS. A tap on a board chip is laid over the Settings default
// and KEPT, across launches: iOS throws the app away whenever it likes, and a
// board that forgot its chips each time read as a board that forgot you. It is
// its own key, so the Settings default underneath never moves; choosing a
// default in Settings drops the tap over it (`write`). Module-level, so every
// screen asking "is trams on?" sees the same answer.
export const TAPS_KEY = 'lateagain:mode-taps:v1';
const readTaps = () => {
  const raw = prefs[TAPS_KEY];
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...raw } : {};
};
const session = ref(readTaps());
function setTaps(next) { session.value = next; prefs[TAPS_KEY] = next; }

/** Carry a planner switch somebody already set into the shared setting, once. */
function migrate(chips, cityId) {
  const old = prefs[OLD_PLAN_KEY];
  if (!old || typeof old !== 'object' || Object.keys(stored.value).some((k) => k.startsWith(`${cityId}:`))) return;
  const next = { ...stored.value };
  for (const c of chips) {
    const key = c.id === 'train' ? 'trains' : c.mode?.planner?.key;
    if (key && typeof old[key] === 'boolean') next[choiceKey(cityId, c.id)] = old[key];
  }
  stored.value = next;
  prefs[FILTER_KEY] = next;
}

/** Test seam: forget the in-memory copy and read storage again. */
export function reloadModeFilter() { stored.value = read(); storedOrder.value = readOrder(); session.value = readTaps(); }

/**
 * @param {object} [o]
 * @param {string[]|(() => string[])|null} [o.only] the chips this screen can honour (ids, or a function
 *   returning them when they change), else all
 */
export function useModeFilter({ only = null } = {}) {
  const chips = computed(() => filterChips(city.value, { only: typeof only === 'function' ? only() : only }));
  migrate(filterChips(city.value), city.value);
  // A SESSION-ONLY OVERRIDE for this screen (`solo`). It is a full on/off map held in this
  // composable's own ref, so it never reaches `prefs` and no other screen sees it; it goes
  // with the screen. While it is set, chip taps edit it rather than the stored setting.
  const local = ref(null);
  const resolved = computed(() => resolveOn(chips.value, stored.value, city.value, modeOn));
  // What the board shows: the defaults, with the board's chip taps laid over them.
  const shown = computed(() => {
    const out = { ...resolved.value };
    for (const c of chips.value) {
      const tap = session.value[choiceKey(city.value, c.id)];
      if (typeof tap === 'boolean') out[c.id] = tap;
    }
    return chips.value.some((c) => out[c.id]) ? out : resolved.value;
  });
  const on = computed(() => {
    if (!local.value) return shown.value;
    const out = {};
    for (const c of chips.value) out[c.id] = local.value[c.id] === true;
    return chips.value.some((c) => out[c.id]) ? out : shown.value;
  });

  const isOn = (id) => on.value[id] !== false;
  const ids = computed(() => activeIds(chips.value, on.value));

  function write(id, value) {
    const key = choiceKey(city.value, id);
    const merged = { ...stored.value, [key]: Boolean(value) };
    // An explicit choice made here replaces any tap made on the board.
    if (key in session.value) { const { [key]: _gone, ...rest } = session.value; setTaps(rest); }
    stored.value = merged;
    prefs[FILTER_KEY] = merged;
  }

  /** Flip a chip; the last one on stays on. */
  function toggle(id) {
    const next = flipped(on.value, id);
    if (next === on.value) return;
    if (local.value) local.value = next;
    else write(id, next[id]);
  }

  /** Make sure the phone has a mode's data, once it is to be shown. Never turns an opt-in off. */
  function optIn(chip) {
    if (chip.mode?.optIn && !modeOn(chip.mode)) {
      markModeIntroSeen(chip.mode);
      setModeOn(chip.mode, true);
    }
  }

  /**
   * A board chip tap: show or hide that mode's rows, remembered across launches
   * until it is tapped back or a default is chosen in Settings. The Settings
   * default is untouched. Showing a mode opts the
   * phone in to it, so its data loads; hiding one touches nothing else. The last
   * one on stays on. Returns whether anything changed.
   */
  function setEnabled(id) {
    const chip = chips.value.find((c) => c.id === id);
    if (!chip) return false;
    const next = flipped(enabled.value, id);
    if (next === enabled.value) return false;
    const value = next[id];
    if (value) {
      // Opting in would make an unset default follow it (on); pin the default as it stands first.
      if (typeof stored.value[choiceKey(city.value, id)] !== 'boolean') write(id, resolved.value[id]);
      optIn(chip);
    }
    if (local.value) local.value = { ...on.value, [id]: value };
    else setTaps({ ...session.value, [choiceKey(city.value, id)]: value });
    return true;
  }

  /**
   * Settings > Transport: whether a mode shows on the board BY DEFAULT. It
   * writes the stored default and nothing else: no watched route, line or alert
   * is ever removed. Turning one on opts the phone in (so its data loads);
   * turning one off leaves the opt-in alone. The last one on stays on.
   */
  function setDefault(id) {
    const chip = chips.value.find((c) => c.id === id);
    if (!chip) return false;
    const next = flipped(defaults.value, id);
    if (next === defaults.value) return false;
    if (next[id]) optIn(chip);
    write(id, next[id]);
    return true;
  }

  /** Show only this chip, for this screen's visit; nothing is stored. False if there is no such chip. */
  function solo(id) {
    if (!chips.value.some((c) => c.id === id)) return false;
    local.value = Object.fromEntries(chips.value.map((c) => [c.id, c.id === id]));
    return true;
  }

  /** Turn a chip on (the hidden row's "Show trams"). */
  function show(id) {
    if (!isOn(id)) toggle(id);
  }

  /** Make sure a chip is on without disturbing the rest (a deep link to a mode). */
  const ensure = show;

  // THE OPENING PRIORITY. The same chips in the order the person ranked them
  // (Settings > Transport), and the ids that are on, in that order.
  const ordered = computed(() => orderChips(chips.value, storedOrder.value[city.value]));
  // What the chips mean app-wide: the filter AND the phone having the mode.
  const enabled = computed(() => effectiveOn(chips.value, on.value, modeOn));
  const isEnabled = (id) => enabled.value[id] === true;
  // The Settings defaults, which is also what the opening priority reads.
  const defaults = computed(() => effectiveOn(chips.value, resolved.value, modeOn));
  const priority = computed(() => priorityIds(ordered.value, defaults.value));
  function reorder(from, to) {
    const ids = ordered.value.map((c) => c.id);
    const next = moveId(ids, from, to);
    if (next === ids) return;
    storedOrder.value = { ...storedOrder.value, [city.value]: next };
    prefs[ORDER_KEY] = storedOrder.value;
  }

  const partition = (rows, modeIdOf) => partitionRows(rows, chips.value, on.value, modeIdOf);
  const notes = (hidden, kind) => hiddenNotes(chips.value, hidden, kind);

  return { chips, ordered, priority, reorder, write, setEnabled, setDefault, defaults, enabled, isEnabled, on, ids, isOn, toggle, show, ensure, solo, partition, notes };
}
